import { captureError } from 'helpers/error'
import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { AuthStore } from 'stores'
import { clearAuthToken } from 'util/auth/cookies'
import { useInstance } from 'util/di'

import { useDeleteApiV1Session } from 'api'

export const useSignOut = (): ((redirectTo?: string) => Promise<void>) => {
  const navigate = useNavigate()
  const authStore = useInstance<AuthStore>('AuthStore')
  const { clearSession, setRefreshAttempted } = authStore

  // An explicit header rather than the RestfulProvider's: app.tsx signs out from the
  // isNotAllowed effect, which runs outside that provider. The token is read from the
  // store when the request is sent: useMutate keeps the requestOptions from its first
  // render (it compares functions by source text), when App has no session yet.
  const { mutate: deleteSession } = useDeleteApiV1Session({
    requestOptions: () => {
      const token = authStore.session?.apiToken
      const headers: Record<string, string> = token
        ? { Authorization: 'Bearer ' + token }
        : {}

      return { headers }
    },
  })

  return useCallback(
    async (redirectTo = '/') => {
      // Before any await; see AuthStore.refreshAttempted.
      setRefreshAttempted(true)

      // Awaited so the remember cookie is gone before anything can read it, but caught
      // so an offline user is never trapped in the app. If this fails the local session
      // still ends and the device stays remembered until the cookie expires.
      try {
        await deleteSession()
      } catch (error) {
        captureError(error, 'Sign out request failed - util/auth/useSignOut.ts')
      }

      clearSession()
      clearAuthToken()
      navigate(redirectTo)
    },
    [clearSession, setRefreshAttempted, navigate, deleteSession]
  )
}
